import { createContext, forwardRef, useContext, type ComponentPropsWithoutRef, type ElementRef } from "react";
import { ToggleGroup as RxToggleGroup } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/** A segmented control: the selected item sits raised on a sand track. `outline` draws each item as its own bordered button. */
export const toggleGroupItemVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium text-sand-700 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:text-aeblack-900 data-[state=on]:bg-white data-[state=on]:text-aegold-800 data-[state=on]:shadow-[0_1px_2px_rgb(43_38_32/0.12)]",
        outline: "border border-sand-300 bg-white hover:bg-sand-50 data-[state=on]:border-aegold-400 data-[state=on]:bg-aegold-50 data-[state=on]:text-aegold-900",
      },
      size: {
        default: "h-8 px-3 text-sm",
        sm: "h-7 px-2.5 text-xs",
        lg: "h-10 px-4 text-sm",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

type ItemVariants = VariantProps<typeof toggleGroupItemVariants>;
const ToggleGroupContext = createContext<ItemVariants>({ variant: "default", size: "default" });

export const ToggleGroup = forwardRef<ElementRef<typeof RxToggleGroup.Root>, ComponentPropsWithoutRef<typeof RxToggleGroup.Root> & ItemVariants>(
  ({ className, variant, size, children, ...props }, ref) => (
    <RxToggleGroup.Root
      ref={ref}
      className={cn("inline-flex items-center gap-0.5", (variant ?? "default") === "default" && "rounded-lg bg-sand-100 p-0.5", variant === "outline" && "gap-1.5", className)}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size }}>{children}</ToggleGroupContext.Provider>
    </RxToggleGroup.Root>
  ),
);
ToggleGroup.displayName = "ToggleGroup";

export const ToggleGroupItem = forwardRef<ElementRef<typeof RxToggleGroup.Item>, ComponentPropsWithoutRef<typeof RxToggleGroup.Item> & ItemVariants>(
  ({ className, variant, size, ...props }, ref) => {
    const group = useContext(ToggleGroupContext);
    return <RxToggleGroup.Item ref={ref} className={cn(toggleGroupItemVariants({ variant: variant ?? group.variant, size: size ?? group.size }), className)} {...props} />;
  },
);
ToggleGroupItem.displayName = "ToggleGroupItem";
